/**
 * Animated dot field ("quicksand"): a grid of white, blue and dark-blue dots
 * drifting on slow overlapping waves. Each dot keeps its own clock, which runs
 * at a crawl far from the pointer and speeds up to normal pace near it, while
 * the pointer also pushes nearby dots aside on a spring — like disturbing sand.
 *
 * Pure canvas, no dependencies. Pauses when the tab is hidden and renders a
 * single still frame when the user prefers reduced motion.
 */
import type { JSX } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { resolvedTheme } from "../prefs";

const SPACING = 18; // px between grid dots
const RADIUS = 170; // pointer influence radius
const SLOW = 0.22; // clock speed far from the pointer
const PUSH = 34; // max displacement away from the pointer (px)
const ALPHA_BUCKETS = 5;

// [white, blue, dark blue] per theme; "white" becomes pale blue on light paper.
const PALETTE = {
  dark: ["#ffffff", "#5b7cff", "#2a4bc4"],
  light: ["#9fb3ff", "#3b5bdb", "#1e3a8a"],
} as const;

export function DotField({ class: cls = "" }: { class?: string }): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0;
    let h = 0;
    let n = 0;
    // Per-dot state in flat typed arrays (thousands of dots, no GC churn).
    let bx = new Float32Array(0);
    let by = new Float32Array(0);
    let phase = new Float32Array(0);
    let clock = new Float32Array(0);
    let dx = new Float32Array(0);
    let dy = new Float32Array(0);
    let kind = new Uint8Array(0);

    const pointer = { x: -9999, y: -9999, active: 0 };

    function layout(): void {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas!.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas!.width = Math.round(w * dpr);
      canvas!.height = Math.round(h * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cols = Math.ceil(w / SPACING) + 2;
      const rows = Math.ceil(h / SPACING) + 2;
      n = cols * rows;
      bx = new Float32Array(n);
      by = new Float32Array(n);
      phase = new Float32Array(n);
      clock = new Float32Array(n);
      dx = new Float32Array(n);
      dy = new Float32Array(n);
      kind = new Uint8Array(n);
      let i = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++, i++) {
          // Offset every other row and jitter a little so it reads as grains, not a grid.
          bx[i] = (c - 1) * SPACING + (r % 2 ? SPACING / 2 : 0) + (Math.random() - 0.5) * 6;
          by[i] = (r - 1) * SPACING + (Math.random() - 0.5) * 6;
          phase[i] = Math.random() * Math.PI * 2;
          clock[i] = Math.random() * 10;
          const roll = Math.random();
          kind[i] = roll < 0.18 ? 0 : roll < 0.5 ? 1 : 2;
        }
      }
    }

    function frame(dt: number): void {
      const colors = PALETTE[resolvedTheme()];
      const paths: Path2D[][] = colors.map(() => Array.from({ length: ALPHA_BUCKETS }, () => new Path2D()));
      const r2 = RADIUS * RADIUS;
      // Ease the pointer's presence in and out so leaving the page settles gently.
      pointer.active += ((pointer.x > -9000 ? 1 : 0) - pointer.active) * Math.min(1, dt * 3);

      for (let i = 0; i < n; i++) {
        const x0 = bx[i]!;
        const y0 = by[i]!;
        const ddx = x0 - pointer.x;
        const ddy = y0 - pointer.y;
        const d2 = ddx * ddx + ddy * ddy;
        const infl = d2 < r2 * 4 ? Math.exp(-d2 / r2) * pointer.active : 0;

        // Local clock: quicksand-slow far away, normal speed under the pointer.
        const tt = (clock[i] = clock[i]! + dt * (SLOW + (1 - SLOW) * infl));

        // Two crossing swells plus a per-dot wobble.
        const swell = Math.sin(x0 * 0.009 + y0 * 0.012 + tt * 0.9);
        const wx = Math.sin(y0 * 0.017 + tt * 0.8 + Math.sin(x0 * 0.005 + tt * 0.25) * 2) * 7;
        const wy = Math.cos(x0 * 0.015 + tt * 0.6 + phase[i]!) * 6 + swell * 4;

        // Spring toward a target pushed away from the pointer.
        const dist = Math.sqrt(d2) || 1;
        const tx = (ddx / dist) * PUSH * infl;
        const ty = (ddy / dist) * PUSH * infl;
        const k = Math.min(1, dt * 4);
        dx[i] = dx[i]! + (tx - dx[i]!) * k;
        dy[i] = dy[i]! + (ty - dy[i]!) * k;

        const x = x0 + wx + dx[i]!;
        const y = y0 + wy + dy[i]!;
        if (x < -4 || y < -4 || x > w + 4 || y > h + 4) continue;

        const crest = (swell + 1) / 2; // 0..1, brighter and larger on crests
        const size = 0.9 + crest * 1.3 + infl * 1.1;
        const alpha = Math.min(1, 0.32 + crest * 0.55 + infl * 0.45);
        const bucket = Math.min(ALPHA_BUCKETS - 1, Math.floor(alpha * ALPHA_BUCKETS));
        const p = paths[kind[i]!]![bucket]!;
        p.moveTo(x + size, y);
        p.arc(x, y, size, 0, Math.PI * 2);
      }

      ctx!.clearRect(0, 0, w, h);
      for (let c = 0; c < colors.length; c++) {
        ctx!.fillStyle = colors[c]!;
        for (let b = 0; b < ALPHA_BUCKETS; b++) {
          ctx!.globalAlpha = (b + 0.5) / ALPHA_BUCKETS;
          ctx!.fill(paths[c]![b]!);
        }
      }
      ctx!.globalAlpha = 1;
    }

    layout();
    const ro = new ResizeObserver(() => {
      layout();
      if (reduced) frame(0);
    });
    ro.observe(canvas);

    const onMove = (e: PointerEvent): void => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
    };
    const onLeave = (): void => {
      pointer.x = -9999;
      pointer.y = -9999;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    window.addEventListener("blur", onLeave);

    let raf = 0;
    let last = performance.now();
    const loop = (now: number): void => {
      // Clamp dt so a backgrounded tab doesn't jump the sand on return.
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      frame(dt);
      raf = requestAnimationFrame(loop);
    };
    if (reduced) frame(0);
    else raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
    };
  }, []);

  return <canvas ref={ref} class={`pointer-events-none block h-full w-full ${cls}`} aria-hidden="true" />;
}
